import isEmpty from 'lodash-es/isEmpty';
import { type PageLayoutDetail, type LayoutNode } from 'shop-ui/types';
import { ELEMENT_TYPE, FEATURE_FLAG_KEYS, LAYOUT_SECTION_NAME } from '../constants';

export class Formatter {
    layoutData: PageLayoutDetail | null;

    effectiveLayoutNodes: string[] = [];

    constructor(layoutData: PageLayoutDetail) {
        this.layoutData = layoutData;

        this.effectiveLayoutNodes = [];
    }

    parseStructure() {
        if (!this.layoutData) {
            return this;
        }

        const { layout: layoutDetail } = this.layoutData;

        const run = (node: LayoutNode) => {
            if (!node || typeof node !== 'object') {
                return;
            }

            this.effectiveLayoutNodes.push(node.id);

            if (node.next && Array.isArray(node.next)) {
                node.next.forEach(run);
            }
        };

        if (layoutDetail.layout) {
            if (Array.isArray(layoutDetail.layout)) {
                layoutDetail.layout.forEach(run);
            } else {
                run(layoutDetail.layout);
            }
        }

        return this;
    }

    pruneUnlistedStyleIdKeys() {
        if (!this.layoutData) {
            return this;
        }

        const { layout: layoutDetail, sectionName } = this.layoutData;

        const { style, layout } = layoutDetail;

        Object.keys(style).forEach((key) => {
            if (!this.effectiveLayoutNodes.includes(key)) {
                delete style[key];
            }
        });

        /*
            暫時移除 header 能夠設定的背景樣式
            相關討論：https://app.asana.com/1/5180948870484/project/1209320197619741/task/1210758238409683?focus=true
        */
        if (sectionName === LAYOUT_SECTION_NAME.HEADER) {
            const removeHeaderStyleKeys = [
                FEATURE_FLAG_KEYS.BACKGROUND_COLOR_GRADIENT,
                FEATURE_FLAG_KEYS.BACKGROUND_IMAGE,
                FEATURE_FLAG_KEYS.IMAGE_FIT,
                FEATURE_FLAG_KEYS.IMAGE_POSITION,
                FEATURE_FLAG_KEYS.BACKGROUND_VIDEO,
            ];

            let headerId = '';

            if (Array.isArray(layout)) {
                layout.forEach((node) => {
                    if (node.type === ELEMENT_TYPE.GRID_PRIMARY_LEVEL) {
                        headerId = node.id;
                    }
                });
            } else if (layout.type === ELEMENT_TYPE.GRID_PRIMARY_LEVEL) {
                headerId = layout.id;
            }

            if (headerId) {
                removeHeaderStyleKeys.forEach((key) => {
                    delete style[headerId][key];
                });
            }
        }

        return this;
    }

    pruneUnlistedContentIdKeys() {
        if (!this.layoutData) {
            return this;
        }

        const { layout: layoutDetail } = this.layoutData;

        const { content } = layoutDetail;

        Object.keys(content).forEach((key) => {
            if (!this.effectiveLayoutNodes.includes(key)) {
                delete content[key];
            }
        });

        return this;
    }

    pruneFalsyValueInStyle() {
        if (!this.layoutData) {
            return this;
        }

        const { layout: layoutDetail } = this.layoutData;

        const { style } = layoutDetail;

        Object.entries(style).forEach(([key, value]) => {
            if (isEmpty(value)) {
                delete style[key];
            }

            Object.entries(value).forEach(([featureKey, featureValue]) => {
                if (typeof featureValue === 'boolean' && !featureValue) {
                    delete style[key][featureKey];
                }

                if (typeof featureValue === 'string' && featureValue === '') {
                    delete style[key][featureKey];
                }

                if (typeof featureValue === 'undefined') {
                    delete style[key][featureKey];
                }
            });
        });

        return this;
    }

    removeLegacyKeys() {
        if (!this.layoutData) {
            return this;
        }

        const { layout: layoutDetail } = this.layoutData;

        const legacyKeys = ['feature'];

        legacyKeys.forEach((key) => {
            if (Object.prototype.hasOwnProperty.call(layoutDetail, key)) {
                delete layoutDetail[key];
            }
        });

        return this;
    }

    removeLegacyElementTypeInLayout() {
        const legacyElementType = ['header-icon-tool'];

        const legacyElementIds: string[] = [];

        if (!this.layoutData) {
            return this;
        }

        const { layout: layoutDetail } = this.layoutData;

        const run = (node: LayoutNode) => {
            if (!node || typeof node !== 'object') {
                return;
            }

            if (node.type === 'grid-secondary-level-inner' && node.next) {
                // eslint-disable-next-line no-param-reassign
                node.next = node.next?.filter((next) => {
                    const isLegacyElement = legacyElementType.includes(next.type);

                    if (isLegacyElement) {
                        legacyElementIds.push(next.id);
                    }

                    return !isLegacyElement;
                });

                return;
            }

            if (node.next && Array.isArray(node.next)) {
                node.next.forEach(run);
            }
        };

        if (layoutDetail.layout) {
            if (Array.isArray(layoutDetail.layout)) {
                layoutDetail.layout.forEach(run);
            } else {
                run(layoutDetail.layout);
            }
        }

        if (legacyElementIds.length > 0) {
            legacyElementIds.forEach((id) => {
                delete layoutDetail.style[id];
            });
        }

        return this;
    }

    transformCompatibleData() {
        if (!this.layoutData) {
            return this;
        }

        const legacyElementConfig = [
            {
                sectionName: LAYOUT_SECTION_NAME.FOOTER,
                type: ELEMENT_TYPE.LOGO,
                layout: [
                    {
                        deletable: true,
                    },
                    {
                        deletable: false,
                    },
                ],
            },
        ];

        const { layout: layoutDetail, sectionName: currentSectionName } = this.layoutData;

        const run = (node: LayoutNode) => {
            if (!node || typeof node !== 'object') {
                return;
            }

            if (node.type === 'grid-secondary-level-inner' && node.next) {
                // eslint-disable-next-line no-param-reassign
                node.next?.forEach((next) => {
                    const transformData = legacyElementConfig.find(
                        (config) =>
                            config.sectionName === currentSectionName &&
                            config.type === next.type
                    );

                    if (transformData && transformData.layout) {
                        const [legacyProperties, newProperties] = transformData.layout;

                        Object.entries(newProperties).forEach(([key, value]) => {
                            if (newProperties[key] !== legacyProperties[key]) {
                                // eslint-disable-next-line no-param-reassign
                                next[key] = value;
                            }
                        });
                    }
                });

                return;
            }

            if (node.next && Array.isArray(node.next)) {
                node.next.forEach(run);
            }
        };

        if (layoutDetail.layout) {
            if (Array.isArray(layoutDetail.layout)) {
                layoutDetail.layout.forEach(run);
            } else {
                run(layoutDetail.layout);
            }
        }

        return this;
    }

    getFormattedLayoutData() {
        if (!this.layoutData) {
            return null;
        }

        return this.layoutData;
    }
}
